// Agent loop — the core "call LLM, execute tools, repeat" runtime.
//
// This is the studio-open equivalent of what `claude -p` does in apps/
// console. It's deliberately small (~150 lines) because all the heavy
// work — provider HTTP, tool implementations, system-prompt building —
// lives in dedicated modules. The loop's only job is sequencing.
//
// One call to runAgentTurn = one user message. The loop:
//   1. Appends the user message to the conversation history.
//   2. Calls the provider to stream a response.
//   3. While the response includes tool_use blocks, executes those
//      tools, appends their results to history, and asks the provider
//      to continue (still the same user turn).
//   4. Once the provider produces a response with no tool calls,
//      emits 'turn_complete'.
//
// Cancellation: pass an AbortSignal; both provider streaming and any
// in-flight tool execution check it.

import type {
  AssistantContentBlock,
  AssistantMessage,
  LLMProvider,
  LoopEvent,
  Message,
  ToolDefinition,
  ToolResultMessage,
  UsageReport,
} from './types';

export interface RunAgentTurnArgs {
  /** Provider that translates messages → LLM and back. */
  provider: LLMProvider;
  /**
   * Conversation history *including* the new user message at the end.
   * Caller is responsible for managing this array (we mutate it by
   * pushing assistant + tool_result messages so the next turn sees them).
   */
  history: Message[];
  /** Available tools the model can call. */
  tools: ToolDefinition[];
  /** Wired to UI / store / persistence. Called for every LoopEvent. */
  onEvent: (e: LoopEvent) => void;
  /** Hard stop. Aborting cancels the provider stream + skips pending tools. */
  signal?: AbortSignal;
  /** Hard cap on tool-call rounds within one user turn. Prevents infinite loops. */
  maxIterations?: number;
}

const DEFAULT_MAX_ITERATIONS = 25;

export async function runAgentTurn(args: RunAgentTurnArgs): Promise<void> {
  const { provider, history, tools, onEvent, signal } = args;
  const maxIterations = args.maxIterations ?? DEFAULT_MAX_ITERATIONS;

  for (let iter = 0; iter < maxIterations; iter++) {
    if (signal?.aborted) {
      onEvent({ kind: 'error', message: '已停止' });
      return;
    }

    // Accumulators for this round of streaming.
    const assembled: AssistantContentBlock[] = [];
    let currentText = '';
    const toolUseBuffers = new Map<string, { name: string; partialJson: string }>();
    let usage: UsageReport | null = null;

    try {
      await provider.streamChat({
        messages: history,
        tools,
        signal,
        onEvent: (pe) => {
          switch (pe.type) {
            case 'text_delta':
              currentText += pe.text;
              onEvent({ kind: 'assistant_text_delta', text: pe.text });
              break;
            case 'tool_use_start':
              // Flush any pending text before recording the tool call.
              if (currentText) {
                assembled.push({ type: 'text', text: currentText });
                onEvent({ kind: 'assistant_text_complete', text: currentText });
                currentText = '';
              }
              toolUseBuffers.set(pe.id, { name: pe.name, partialJson: '' });
              break;
            case 'tool_use_input_delta': {
              const buf = toolUseBuffers.get(pe.id);
              if (buf) buf.partialJson += pe.partialJson;
              break;
            }
            case 'tool_use_complete':
              toolUseBuffers.delete(pe.id);
              assembled.push({
                type: 'tool_use',
                id: pe.id,
                name: pe.name,
                input: pe.input,
              });
              onEvent({
                kind: 'tool_call',
                id: pe.id,
                name: pe.name,
                input: pe.input,
              });
              break;
            case 'message_complete':
              if (currentText) {
                assembled.push({ type: 'text', text: currentText });
                onEvent({ kind: 'assistant_text_complete', text: currentText });
                currentText = '';
              }
              usage = pe.usage;
              break;
          }
        },
      });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError' || signal?.aborted) {
        onEvent({ kind: 'error', message: '已停止' });
        return;
      }
      onEvent({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
      return;
    }

    if (usage) onEvent({ kind: 'usage', report: usage });

    // Persist this assistant turn into history regardless of tool use.
    const assistantMessage: AssistantMessage = { role: 'assistant', content: assembled };
    history.push(assistantMessage);

    // Find tool_use blocks that need execution.
    const toolUses = assembled.filter(
      (b): b is Extract<AssistantContentBlock, { type: 'tool_use' }> =>
        b.type === 'tool_use',
    );

    if (toolUses.length === 0) {
      onEvent({ kind: 'turn_complete' });
      return;
    }

    // Execute all tool calls (sequentially — keeps tool effects ordered
    // and matches model expectations of a single tool_result message
    // following a tool_use block). Push tool_result messages into history.
    for (const tu of toolUses) {
      if (signal?.aborted) {
        onEvent({ kind: 'error', message: '已停止' });
        return;
      }
      const tool = tools.find((t) => t.name === tu.name);
      let resultContent: string;
      let isError = false;
      if (!tool) {
        resultContent = `Error: tool '${tu.name}' is not registered`;
        isError = true;
      } else {
        try {
          resultContent = await tool.execute(tu.input);
        } catch (err) {
          resultContent = err instanceof Error ? err.message : String(err);
          isError = true;
        }
      }
      onEvent({ kind: 'tool_result', id: tu.id, content: resultContent, isError });
      const toolResult: ToolResultMessage = {
        role: 'tool_result',
        toolUseId: tu.id,
        content: resultContent,
        isError,
      };
      history.push(toolResult);
    }

    // Loop continues — provider sees the new tool_results in history.
  }

  onEvent({
    kind: 'error',
    message: `Agent loop hit max iterations (${maxIterations}); aborting to prevent runaway.`,
  });
}
